// Push notification utilities for Penticton Dev Events

// VAPID public key from environment variables
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class PushNotificationManager {
  private swRegistration: ServiceWorkerRegistration | null = null;

  /**
   * Initialize the service worker and push notifications
   */
  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return false;
    }

    if (!('PushManager' in window)) {
      console.warn('Push API not supported');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.swRegistration);

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  /**
   * Check if push notifications are supported and permitted
   */
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    );
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscriptionData | null> {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    if (!VAPID_PUBLIC_KEY) {
      throw new Error(
        'VAPID public key not configured. Please set VITE_VAPID_PUBLIC_KEY environment variable.',
      );
    }

    if (Notification.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }
    }

    try {
      const applicationServerKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      const subscriptionData = this.subscriptionToData(subscription);
      console.log('Push subscription created:', subscriptionData);

      return subscriptionData;
    } catch (error) {
      console.error('Push subscription failed:', error);
      if (error instanceof Error && error.message.includes('applicationServerKey')) {
        throw new Error(
          'Invalid VAPID public key. Please check your VITE_VAPID_PUBLIC_KEY configuration.',
        );
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        const result = await subscription.unsubscribe();
        console.log('Push subscription removed:', result);
        return result;
      }
      return true;
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      return false;
    }
  }

  /**
   * Get current push subscription
   */
  async getSubscription(): Promise<PushSubscriptionData | null> {
    if (!this.swRegistration) {
      return null;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        return this.subscriptionToData(subscription);
      }
      return null;
    } catch (error) {
      console.error('Failed to get push subscription:', error);
      return null;
    }
  }

  /**
   * Show a test notification
   */
  async showTestNotification(): Promise<void> {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    try {
      // Try service worker notification first
      await this.swRegistration.showNotification('🔔 Test Notification', {
        body: 'This is a test notification from Penticton Dev Events! Push notifications are working correctly.',
        tag: 'test',
        requireInteraction: true,
        data: {
          url: '/',
          timestamp: Date.now(),
        },
      });
    } catch (swError) {
      console.warn(
        'Service Worker notification failed, falling back to browser notification:',
        swError,
      );

      // Fallback to browser notification API
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🔔 Test Notification', {
          body: 'This is a test notification from Penticton Dev Events! Push notifications are working correctly.',
          tag: 'test',
        });
      } else {
        throw new Error('Neither Service Worker nor browser notifications are available');
      }
    }
  }

  /**
   * Convert subscription to serializable data
   */
  private subscriptionToData(subscription: PushSubscription): PushSubscriptionData {
    const keys = subscription.getKey.bind(subscription);

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(keys('p256dh')),
        auth: this.arrayBufferToBase64(keys('auth')),
      },
    };
  }

  /**
   * Convert VAPID key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer | null): string {
    if (!buffer) return '';

    const bytes = new Uint8Array(buffer);
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i] ?? 0);
    }
    return window.btoa(binary);
  }
}

// Create singleton instance
export const pushNotificationManager = new PushNotificationManager();
